/*
 * The camp / stay search the user made: dates + guests.
 *
 * Carried in the URL on every camp page
 *   /search-result/camp?place=Munnar&checkin=2026-10-07&checkout=2026-10-10&adults=2&children=0&pets=0
 *   /camp/details/:id?checkin=...&checkout=...&adults=...
 * and also kept in sessionStorage, so it's still filled in if the user
 * opens a page without them (back button, new tab from the same session).
 *
 *   readCampStay(searchParams)  -> { place, checkin, checkout, adults, children, pets }
 *   saveCampStay(stay)          -> remember it for this browser session
 *   campStayQuery(stay)         -> "checkin=...&checkout=...&adults=..."
 *
 * The units picked can ride along too (Change Details on the review page):
 *   ?units=<unitId>-3.<unitId>-2&reserve=1
 *   unitsQuery(items)           -> "<unitId>-3.<unitId>-2"
 *   readUnitSelection(params)   -> { [unitId]: rooms }
 */
import moment from 'moment';

const KEY = 'trvto_camp_stay';
// dates from the URL can be 2026-10-07 (new) or 07102026 (old links)
const DATE_FORMATS = ['YYYY-MM-DD', 'DDMMYYYY'];

const toDate = (value) => {
    if (!value) return null;
    // moment / Date objects (from date pickers) or strings
    const m =
        moment.isMoment(value) || value instanceof Date ? moment(value) : moment(String(value), DATE_FORMATS, true);
    return m.isValid() ? m.format('YYYY-MM-DD') : null;
};

const toCount = (value, min = 0) => {
    const n = Number(value);
    return Number.isFinite(n) && n >= min ? Math.floor(n) : null;
};

const readSaved = () => {
    try {
        return JSON.parse(sessionStorage.getItem(KEY) || 'null') || {};
    } catch (e) {
        return {};
    }
};

/** Dates + guests from the URL, else from this session's last camp search. */
export function readCampStay(searchParams) {
    const get = (k) => (searchParams?.get ? searchParams.get(k) : null);
    const saved = readSaved();

    let checkin = toDate(get('checkin'));
    let checkout = toDate(get('checkout'));
    if (!checkin && !checkout) {
        checkin = toDate(saved.checkin);
        checkout = toDate(saved.checkout);
    }

    // past or wrong dates are dropped (the user picks new ones)
    const today = moment().format('YYYY-MM-DD');
    if (!checkin || checkin < today || !checkout || checkout <= checkin) {
        checkin = null;
        checkout = null;
    }

    let adults = toCount(get('adults'), 1);
    let children = toCount(get('children'));
    let pets = toCount(get('pets'));

    // old links: guests=2-1_2-0 (adults-children per room)
    const legacy = get('guests');
    if (adults === null && legacy) {
        const totals = legacy.split('_').reduce(
            (acc, room) => {
                const [a, c] = room.split('-');
                return { adults: acc.adults + (Number(a) || 0), children: acc.children + (Number(c) || 0) };
            },
            { adults: 0, children: 0 }
        );
        adults = totals.adults || null;
        children = totals.children;
    }

    const hasUrlGuests = adults !== null;
    const place = String(get('place') || saved.place || '').trim().slice(0, 100) || null;
    return {
        // the place searched (URL, else this session's last search)
        place,
        checkin,
        checkout,
        adults: adults ?? toCount(saved.adults, 1) ?? 1,
        children: (hasUrlGuests ? children : toCount(saved.children)) ?? 0,
        pets: (hasUrlGuests ? pets : toCount(saved.pets)) ?? 0,
        // true when anything came from the URL or an earlier search
        found: Boolean(checkin || hasUrlGuests || saved.adults)
    };
}

/** Remember the stay for this browser session. */
export function saveCampStay({ place, checkin, checkout, adults, children, pets } = {}) {
    try {
        const prev = readSaved();
        const cleanPlace = String(place || '').trim().slice(0, 100);
        sessionStorage.setItem(
            KEY,
            JSON.stringify({
                place: cleanPlace || prev.place || null,
                checkin: toDate(checkin) ?? prev.checkin ?? null,
                checkout: toDate(checkout) ?? prev.checkout ?? null,
                adults: toCount(adults, 1) ?? prev.adults ?? 1,
                children: toCount(children) ?? prev.children ?? 0,
                pets: toCount(pets) ?? prev.pets ?? 0
            })
        );
    } catch (e) {
        // storage blocked — the URL still carries it
    }
}

/** "<unitId>-3.<unitId>-2" from [{ unitId, rooms }] */
export const unitsQuery = (items = []) =>
    items
        .filter((item) => item?.unitId && toCount(item.rooms, 1))
        .map((item) => `${item.unitId}-${toCount(item.rooms, 1)}`)
        .join('.');

/** { [unitId]: rooms } from ?units=..., empty when not there */
export function readUnitSelection(searchParams) {
    const raw = searchParams?.get ? searchParams.get('units') : null;
    if (!raw) return {};
    return raw.split('.').reduce((selection, part) => {
        const [unitId, rooms] = part.split('-');
        const count = toCount(rooms, 1);
        if (unitId && count) selection[unitId] = (selection[unitId] || 0) + count;
        return selection;
    }, {});
}

/** Query string for links to the next camp page. */
export function campStayQuery({ checkin, checkout, adults, children, pets } = {}) {
    const params = new URLSearchParams();
    if (toDate(checkin)) params.set('checkin', toDate(checkin));
    if (toDate(checkout)) params.set('checkout', toDate(checkout));
    params.set('adults', String(toCount(adults, 1) ?? 1));
    if (toCount(children)) params.set('children', String(toCount(children)));
    if (toCount(pets)) params.set('pets', String(toCount(pets)));
    return params.toString();
}
